/* Sizzle player — a poster-first click-to-play HTML5 video with brand
   flourishes.  Fetches metadata from /api/sizzle/meta so the DOM stays quiet
   if the reel hasn't been uploaded yet (avoids a 404 in the console for
   fresh installations). */
import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";

// Prefer the app-wide REACT_APP_BACKEND_URL so <video src> works across
// deployments where the frontend and API live on different domains.
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const metaUrl   = `${API_BASE}/api/sizzle/meta`;
const streamUrl = `${API_BASE}/api/sizzle`;
const posterUrl = `${API_BASE}/api/sizzle/poster`;

export default function SizzlePlayer({
  className = "",
  testId = "sizzle-player",
  autoplayOnClick = true,
}) {
  const [meta, setMeta] = useState(null);   // {available, size_bytes, ...}
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch(metaUrl)
      .then((r) => r.json())
      .then((m) => { if (!cancelled) setMeta(m); })
      .catch(() => setMeta({ available: false }));
    return () => { cancelled = true; };
  }, []);

  if (!meta) {
    return (
      <div className={`dt6-card p-6 ${className}`.trim()} data-testid={`${testId}-loading`}>
        <div className="dt6-skeleton" style={{ height: 240 }} />
      </div>
    );
  }
  if (!meta.available) {
    return null; // Fail-silent — reel not uploaded on this environment.
  }

  const start = () => {
    setPlaying(true);
    requestAnimationFrame(() => {
      if (videoRef.current && autoplayOnClick) {
        videoRef.current.play().catch(() => {});
      }
    });
  };

  return (
    <div className={`dt6-card overflow-hidden ${className}`.trim()} style={{ padding: 0 }} data-testid={testId}>
      <div className="relative" style={{ background: "#000", aspectRatio: "16 / 9" }}>
        {playing ? (
          <video
            ref={videoRef}
            src={streamUrl}
            poster={posterUrl}
            controls
            playsInline
            preload="metadata"
            style={{ width: "100%", height: "100%", display: "block" }}
            data-testid={`${testId}-video`}
          />
        ) : (
          <button
            type="button"
            onClick={start}
            data-testid={`${testId}-play`}
            className="w-full h-full flex items-center justify-center focus:outline-none"
            style={{
              background: `url('${posterUrl}') center/cover no-repeat, #0B0E14`,
              cursor: "pointer",
            }}
            aria-label="Play DT6 sizzle reel"
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 84,
                height: 84,
                borderRadius: "50%",
                background: "rgba(178, 34, 52, 0.94)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.4), 0 0 0 6px rgba(237,232,220,0.06)",
                transition: "transform 200ms ease, background 200ms ease",
              }}
              className="hover:scale-105"
            >
              <Play size={30} strokeWidth={2} fill="#0B0E14" color="#0B0E14" style={{ marginLeft: 5 }} />
            </span>
          </button>
        )}
      </div>
      <div className="px-5 py-4 border-t flex items-center justify-between font-mono text-[0.65rem] tracking-[0.16em] uppercase text-[color:var(--dt6-muted)]" style={{ borderColor: "var(--dt6-border)" }}>
        <span data-testid={`${testId}-eyebrow`}>/ DT6 · SIZZLE · {Math.round(meta.duration_seconds || 0)}s</span>
        <span>{(meta.size_bytes / 1024 / 1024).toFixed(1)} MB · H.264</span>
      </div>
    </div>
  );
}
